import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import FlameMark from "@/components/FlameMark";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { LogOut, ChevronDown, UserCog, Menu } from "lucide-react";
import { useAppVersion, VersionTag, WhatsNewDialog } from "@/components/WhatsNew";

const ROLE_NAV = {
  technician: [{ to: "/tech", label: "My Jobs" }],
  service_coordinator: [
    { to: "/dispatch", label: "Dispatch" },
    { to: "/completed", label: "Completed" },
    { to: "/intake", label: "Intake" },
    { to: "/customers", label: "Customers" },
    { to: "/dashboard", label: "SLA" },
    { to: "/equipment", label: "Equipment" },
  ],
  manager: [
    { to: "/dispatch", label: "Dispatch" },
    { to: "/completed", label: "Completed" },
    { to: "/dashboard", label: "SLA" },
    { to: "/intake", label: "Intake" },
    { to: "/customers", label: "Customers" },
    { to: "/equipment", label: "Equipment" },
    { to: "/import", label: "Import" },
    { to: "/export", label: "Export" },
    { to: "/users", label: "Users" },
    { to: "/settings/equipment-types", label: "Types" },
    { to: "/settings/sla", label: "SLA Rules" },
    { to: "/settings/backup", label: "Backup", ownerOnly: true },
  ],
};

const ROLE_LABELS = {
  service_coordinator: "Service Coordinator",
  technician: "Technician",
  manager: "Manager",
};

function useIsCompact() {
  const query = "(max-width: 767px)";
  const [compact, setCompact] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = event => setCompact(event.matches);
    media.addEventListener("change", update);
    setCompact(media.matches);
    return () => media.removeEventListener("change", update);
  }, []);
  return compact;
}

export default function Layout({ children }) {
  const { user, logout, setRole } = useAuth();
  const appVersion = useAppVersion();
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const canSeeNotes = ["manager", "service_coordinator"].includes(user?.role);
  const navigate = useNavigate();
  const location = useLocation();
  const compact = useIsCompact();
  const [menuOpen, setMenuOpen] = useState(false);
  const isOwner = user?.owner;
  const nav = (ROLE_NAV[user?.role] || ROLE_NAV.technician).filter(n => !n.ownerOnly || user?.owner);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const changeRole = async (r) => {
    await setRole(r);
    const first = ROLE_NAV[r][0].to;
    navigate(first);
  };

  const avatar = (
    <div className="h-7 w-7 rounded-full overflow-hidden flex items-center justify-center text-xs font-semibold"
      style={{ background: "#EAF0FE", color: "#1E3A8A" }}>
      {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "?")}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: "#F6F7F9" }} data-testid="app-layout">
      <header className="sticky top-0 z-30" style={{ background: "#FFFFFF", borderBottom: "1px solid #EFF1F4" }}>
        <div className="max-w-[1500px] mx-auto px-4 md:px-5 h-16 flex items-center gap-4 md:gap-6">
          <Link to="/home" className="flex items-center gap-2.5 min-w-0" data-testid="brand-link">
            <div className="h-10 flex-none flex items-center justify-center">
              <FlameMark size={40} animate />
            </div>
            <div className="leading-none min-w-0">
              <div className="text-[14px] md:text-[15px] font-semibold tracking-tight truncate" style={{ color: "#12161B" }}>
                Blueflames Service Pro
              </div>
              <div className="text-[10px] mono hidden sm:block" style={{ color: "#8A93A1", letterSpacing: "0.08em" }}>BRIDGETOWN · AST</div>
            </div>
          </Link>

          {!compact && (
            <nav className="flex items-center gap-1 ml-4">
              {nav.map(n => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.to.slice(1)}`}
                  className={({ isActive }) =>
                    `bf-nav-pill px-3.5 py-1.5 text-[13px] font-medium ${isActive ? "" : "hover:bg-[#F6F7F9]"}`
                  }
                  style={({ isActive }) => isActive
                    ? { background: "#EAF0FE", color: "#1E3A8A" }
                    : { color: "#5B6472" }}
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          )}

          <div className="flex-1" />

          {compact ? (
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button
                  className="flex items-center gap-2 px-2 rounded-[10px] hover:bg-[#F6F7F9]"
                  style={{ color: "#12161B", minHeight: 44 }}
                  data-testid="user-menu-trigger"
                  aria-label="Open menu"
                >
                  {avatar}
                  <Menu size={20} color="#5B6472" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[290px] p-0 overflow-y-auto [&>button]:h-11 [&>button]:w-11 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:rounded-[10px]" data-testid="user-menu">
                <div className="px-4 py-4 border-b" style={{ borderColor: "#EFF1F4" }}>
                  <SheetTitle className="sr-only">Menu</SheetTitle>
                  <SheetDescription className="sr-only">Navigation and account actions</SheetDescription>
                  <div className="flex items-center gap-2">
                    {avatar}
                    <div className="leading-tight min-w-0">
                      <div className="text-[13.5px] font-medium truncate" style={{ color: "#12161B" }}>{user?.name}</div>
                      <div className="text-[10px] mono" style={{ color: "#5B6472" }}>
                        {ROLE_LABELS[user?.role]}{isOwner && " · OWNER"}
                      </div>
                    </div>
                  </div>
                </div>

                <nav className="p-2">
                  {nav.map(n => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      data-testid={`nav-${n.to.slice(1)}`}
                      className="flex items-center rounded-[12px] px-3 text-[14px] font-medium"
                      style={({ isActive }) => ({
                        minHeight: 44,
                        background: isActive ? "#EAF0FE" : "transparent",
                        color: isActive ? "#1E3A8A" : "#12161B",
                      })}
                    >
                      {n.label}
                    </NavLink>
                  ))}
                </nav>

                {user?.roles?.length > 1 && (
                  <div className="p-2 border-t" style={{ borderColor: "#EFF1F4" }}>
                    <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold px-3 py-1" style={{ color: "#5B6472" }}>
                      Switch workspace
                    </div>
                    {user.roles.map(r => (
                      <button
                        key={r}
                        onClick={() => changeRole(r)}
                        className="w-full flex items-center rounded-[12px] px-3 text-[14px] text-left"
                        style={{ minHeight: 44, color: "#12161B" }}
                        data-testid={`role-switch-${r}`}
                      >
                        {ROLE_LABELS[r]} {user?.role === r ? "✓" : ""}
                      </button>
                    ))}
                  </div>
                )}

                <div className="p-2 border-t" style={{ borderColor: "#EFF1F4" }}>
                  {user?.role === "manager" && (
                    <button
                      onClick={() => navigate("/users")}
                      className="w-full flex items-center gap-2 rounded-[12px] px-3 text-[14px] text-left"
                      style={{ minHeight: 44, color: "#12161B" }}
                      data-testid="user-access-menu-item"
                    >
                      <UserCog size={16} /> Users &amp; Access
                    </button>
                  )}
                  <button
                    onClick={logout}
                    className="w-full flex items-center gap-2 rounded-[12px] px-3 text-[14px] text-left"
                    style={{ minHeight: 44, color: "#12161B" }}
                    data-testid="logout-btn"
                  >
                    <LogOut size={16} /> Sign out
                  </button>
                  <VersionTag version={appVersion} canOpen={canSeeNotes} onOpen={() => setWhatsNewOpen(true)}
                              className="block px-3 pt-3 pb-1 text-left" />
                </div>
              </SheetContent>
            </Sheet>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1.5 rounded-[10px] hover:bg-[#F6F7F9]" style={{ color: "#12161B" }} data-testid="user-menu-trigger">
                  {avatar}
                  <div className="text-left leading-tight">
                    <div className="text-[12px]" style={{ color: "#12161B" }}>{user?.name}</div>
                    <div className="text-[10px] mono" style={{ color: "#5B6472" }}>{ROLE_LABELS[user?.role]}{isOwner && " · OWNER"}</div>
                  </div>
                  <ChevronDown size={14} color="#8A93A1" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60" data-testid="user-menu">
                {user?.roles?.length > 1 ? (
                  <>
                    <DropdownMenuLabel>Switch workspace</DropdownMenuLabel>
                    {user.roles.map(r => (
                      <DropdownMenuItem key={r} onClick={() => changeRole(r)} data-testid={`role-switch-${r}`}>
                        {ROLE_LABELS[r]} {user?.role === r ? "✓" : ""}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                ) : (
                  <DropdownMenuLabel>Signed in as {ROLE_LABELS[user?.role]}</DropdownMenuLabel>
                )}
                {user?.role === "manager" && (
                  <DropdownMenuItem onClick={() => navigate("/users")} data-testid="user-access-menu-item">
                    <UserCog size={14} className="mr-2" /> Users &amp; Access
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
                {appVersion && <DropdownMenuSeparator />}
                {appVersion && canSeeNotes && (
                  <DropdownMenuItem onSelect={() => setWhatsNewOpen(true)} className="mono text-[11px]"
                                    style={{ color: "#8A93A1" }} data-testid="app-version">
                    v{appVersion}
                  </DropdownMenuItem>
                )}
                {appVersion && !canSeeNotes && (
                  <VersionTag version={appVersion} canOpen={false} className="block px-2 py-1 text-left" />
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </header>
      <main className="max-w-[1500px] mx-auto px-4 md:px-7 py-6 md:py-7">{children}</main>
      {canSeeNotes && <WhatsNewDialog open={whatsNewOpen} onOpenChange={setWhatsNewOpen} />}
    </div>
  );
}
